import type {
	IVeevaVaultAssetImport,
	PaginatedResponse,
	VeevaVaultAssetFileDTO,
	VeevaVaultAssetImportCounts,
	VeevaVaultAssetImportStatus,
} from '@atlas/types'
import { apiClient } from './api.client'

export type VeevaVaultAssetImport = Omit<IVeevaVaultAssetImport, 'files'> & { files: VeevaVaultAssetFileDTO[] }

/** One page of imports, and how many imports have each status across every page. */
export interface VeevaVaultAssetImportPage extends PaginatedResponse<VeevaVaultAssetImport> {
	counts: VeevaVaultAssetImportCounts
}

export interface VeevaVaultAssetImportListParams {
	page: number
	status?: VeevaVaultAssetImportStatus
}

export interface StartedVeevaVaultAssetImport {
	/** False when the asset code was already imported and its recorded import came back. */
	started: boolean
	assetImport: VeevaVaultAssetImport
}

const importPath = (assetCode: string) => `/integrations/veeva-vault/assets/${encodeURIComponent(assetCode)}/downloads`

/** ADMIN-only Veeva Vault imports. The server validates every identifier; the page does not need to. */
export const veevaVaultApi = {
	/**
	 * Starts the import (`202`), or returns the one already recorded for the asset code (`200`).
	 * An import whose files were all deleted from the library starts again.
	 */
	startImport: (assetCode: string): Promise<StartedVeevaVaultAssetImport> =>
		apiClient
			.post<{ data: VeevaVaultAssetImport }>(importPath(assetCode))
			.then((r) => ({ started: r.status === 202, assetImport: r.data.data })),

	/** The recorded import of one asset code. */
	getImport: (assetCode: string): Promise<VeevaVaultAssetImport> =>
		apiClient.get<{ data: VeevaVaultAssetImport }>(importPath(assetCode)).then((r) => r.data.data),

	/** A page of recorded imports, most recent first. */
	listImports: ({ page, status }: VeevaVaultAssetImportListParams): Promise<VeevaVaultAssetImportPage> =>
		apiClient
			.get<VeevaVaultAssetImportPage>('/integrations/veeva-vault/assets', {
				params: { page, ...(status ? { 'filters[status]': status } : {}) },
			})
			.then((r) => r.data),
}
